import React, { useRef } from "react";
import {
  Table,
  Tag,
  Image,
  Typography,
  Space,
  Tooltip,
} from "antd";
import { ArrowRightOutlined, CalendarOutlined } from "@ant-design/icons";
import { getTablePagination } from "../../utils/tablePagination";
import bedImg from "../../assets/images/patientIcons/bed.svg";
import docIcon from "../../assets/doctorIcons/consultant.svg";
import dayjs from "dayjs";
import NoDataPage from "../NoDataPages/NoDataPage";
import patientDataIcon from "../../assets/images/nurseIcons/patientdata.svg";
import { UiTag } from "../ui";
import useIsMobile from "../../hooks/useIsMobile";
import usePageFillTableScroll from "../../hooks/usePageFillTableScroll";
import "./NurseView.css";

const formatAgeGender = (ageByDob, gender) => {
  const age = ageByDob != null ? Number(ageByDob) : null;
  const genderShort =
    gender === "Male"
      ? "M"
      : gender === "Female"
      ? "F"
      : gender === "Other"
      ? "Other"
      : "";

  let ageText = "";
  if (age != null && !Number.isNaN(age)) {
    if (age < 1) {
      const months = Math.round(age * 12);
      ageText = `${months} months`;
    } else {
      ageText = `${Math.floor(age)}`;
    }
  }

  if (ageText && genderShort) return `${ageText} / ${genderShort}`;
  if (ageText) return ageText;
  if (genderShort) return genderShort;
  return "";
};

const PatientNameCell = ({ item, onClick }) => {
  const ageGender = formatAgeGender(item?.age_by_dob, item?.gender);

  return (
    <div className="nurse-name-cell" onClick={onClick}>
      <div className="nurse-name-row">
        <Typography.Text strong className="nurse-name-text">
          {item?.patient_name}
        </Typography.Text>
        {item?.bed && (
          <Tag color="orange" className="nurse-meta-tag">
            <Image src={bedImg} preview={false} width={14} /> {item?.bed}
          </Tag>
        )}
      </div>
      <div className="nurse-meta-row">
        {item?.ip_id && (
          <Tag className="nurse-meta-tag">IP ID: {item?.ip_id}</Tag>
        )}
        {ageGender && <Tag className="nurse-meta-tag">{ageGender}</Tag>}
      </div>
    </div>
  );
};

const formatCount = (value) => String(value ?? 0).padStart(2, "0");

const StatusTag = ({ completed }) => (
  <Tag className={completed ? "tag-completed" : "tag-pending"}>
    {completed ? "Completed" : "Pending"}
  </Tag>
);

const CountBadges = ({ upcoming, missed }) => (
  <Space size={6} wrap>
    <Tag className="tag-completed nurse-count-badge">
      Upcoming : {formatCount(upcoming)}
    </Tag>
    <Tag className="tag-pending nurse-count-badge">
      Missed : {formatCount(missed)}
    </Tag>
  </Space>
);

const clickableCell = (onClick) => ({
  onClick: (e) => {
    e.stopPropagation();
    onClick?.();
  },
  style: { cursor: "pointer" },
});

const NursePatientTable = ({
  dataSource = [],
  totalCount = 0,
  loading,
  activeSegment,
  pageNo,
  onPageChange,
  onPatientClick,
  onPatientViewClick,
  truncateName,
}) => {
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = usePageFillTableScroll(tableWrapRef, isMobile);

  const activeColumns = [
    {
      title: "Name",
      key: "name",
      width: 260,
      render: (_, item) => (
        <PatientNameCell
          item={item}
          onClick={() => onPatientClick(item, "landing")}
        />
      ),
    },
    {
      title: "Initial Assesment",
      key: "initial_assessment",
      width: 130,
      onCell: (item) =>
        clickableCell(() => onPatientClick(item, "ia")),
      render: (_, item) => (
        <StatusTag completed={item?.nurse_initial_assessment} />
      ),
    },
    {
      title: "Diet",
      key: "diet",
      width: 100,
      onCell: (item) =>
        clickableCell(() => onPatientClick(item, "diet")),
      render: (_, item) => (
        <StatusTag completed={item?.diet?.completed} />
      ),
    },
    {
      title: "Medicine",
      key: "medicine",
      width: 110,
      onCell: (item) =>
        clickableCell(() => onPatientClick(item, "medicine")),
      render: (_, item) => (
        <StatusTag completed={item?.medication?.completed} />
      ),
    },
    {
      title: "Vital Monitoring",
      key: "monitoring",
      width: 150,
      onCell: (item) =>
        clickableCell(() => onPatientClick(item, "monitoring")),
      render: (_, item) => (
        <StatusTag completed={item?.monitoring?.up_to_date} />
      ),
    },
    {
      title: "Care Items",
      key: "care_items",
      width: 220,
      onCell: (item) =>
        clickableCell(() => onPatientClick(item, "order-sheet")),
      render: (_, item) => (
        <CountBadges
          upcoming={item?.care_items?.upcoming}
          missed={item?.care_items?.missed}
        />
      ),
    },
    {
      title: "Position Change",
      key: "position_change",
      width: 240,
      onCell: (item) =>
        clickableCell(() =>
          onPatientClick(item, "order-sheet", "position_change_schedule")
        ),
      render: (_, item) => (
        <CountBadges
          upcoming={item?.position_change?.upcoming}
          missed={item?.position_change?.missed}
        />
      ),
    },
  ];

  const dischargedColumns = [
    {
      title: "Name",
      key: "name",
      width: 240,
      render: (_, item) => (
        <PatientNameCell
          item={item}
          onClick={() => onPatientViewClick(item)}
        />
      ),
    },
    {
      title: "Admission / Discharge",
      key: "dates",
      width: 260,
      render: (_, item) => (
        <UiTag tone="info" size="sm" style={{ whiteSpace: "normal", height: "auto" }}>
          <CalendarOutlined style={{ marginRight: 4 }} />
          {item?.discharge_date
            ? `${dayjs(item?.admission_date).format("DD MMM, YYYY")} - ${dayjs(
                item?.discharge_date
              ).format("DD MMM, YYYY")}`
            : dayjs(item?.date_of_admission).format("DD MMM, YYYY")}
        </UiTag>
      ),
    },
    {
      title: "Consultant",
      key: "doctor",
      width: 180,
      ellipsis: true,
      render: (_, item) =>
        item?.doctor_name ? (
          <Tooltip
            placement="top"
            title={item?.doctor_name}
            color="#E6F4FF"
            overlayInnerStyle={{ color: "#0d439e" }}
          >
            <UiTag tone="info" size="sm" style={{ cursor: "pointer" }}>
              <Image
                src={docIcon}
                style={{ width: 12, marginRight: 4 }}
                preview={false}
              />
              {truncateName(item?.doctor_name, "consultant")}
            </UiTag>
          </Tooltip>
        ) : null,
    },
    {
      title: "",
      key: "action",
      width: 120,
      render: (_, item) => (
        <Typography.Link
          onClick={(e) => {
            e.stopPropagation();
            onPatientViewClick(item);
          }}
        >
          View EMR <ArrowRightOutlined />
        </Typography.Link>
      ),
    },
  ];

  const columns = activeSegment === "active" ? activeColumns : dischargedColumns;

  return (
    <div ref={tableWrapRef} className="ui-table-fill-body">
      <Table
      className="nurse-patient-table"
      columns={columns}
      dataSource={dataSource}
      rowKey="admission_id"
      loading={loading}
      pagination={
        totalCount > 0
          ? getTablePagination({
              current: pageNo,
              pageSize: 10,
              total: totalCount,
              hideOnSinglePage: totalCount <= 10,
              onChange: onPageChange,
            })
          : false
      }
      scroll={tableScroll}
      locale={{
        emptyText: (
          <NoDataPage
            title="No Patient Data"
            subtitle="Seems like, there aren't any patient data"
            image={patientDataIcon}
          />
        ),
      }}
      onRow={
        activeSegment === "discharged"
          ? (item) => ({
              onClick: () => onPatientViewClick(item),
              style: { cursor: "pointer" },
            })
          : undefined
      }
    />
    </div>
  );
};

export default NursePatientTable;
